import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Loading, EmptyState, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FolderKanban, Plus, Pencil, LocateFixed, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { onErr } from "@/lib/providers";

const TB_ACF_FIELDS = [
  { key: "households_visited", label: "Households visited" },
  { key: "presumptive_referred", label: "Presumptive TB referred" },
  { key: "samples_collected", label: "Sputum samples collected" },
];

const empty = {
  name: "", code: "", client: "", city: "", state: "", centre_latitude: "", centre_longitude: "", radius_km: 25,
  start_date: "", end_date: "", unit_label: "Persons screened", log_fields: [], coordinator_name: "", status: "Active",
};

export default function ProjectsTab() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState(empty);
  const { data: projects, isLoading } = useQuery({ queryKey: ["projects"], queryFn: async () => (await api.get("/projects")).data });

  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const startNew = () => { setEditId(null); setF(empty); setOpen(true); };
  const startEdit = (p) => { setEditId(p.id); setF({ ...empty, ...p }); setOpen(true); };

  const useMyLocation = () => navigator.geolocation?.getCurrentPosition(
    (pos) => setF((x) => ({ ...x, centre_latitude: pos.coords.latitude.toFixed(6), centre_longitude: pos.coords.longitude.toFixed(6) })),
    () => toast.error("Couldn't get your current location."), { enableHighAccuracy: true, timeout: 15000 });

  const save = useMutation({
    mutationFn: async () => {
      const body = { ...f, centre_latitude: Number(f.centre_latitude), centre_longitude: Number(f.centre_longitude),
        radius_km: Number(f.radius_km), log_fields: f.log_fields.filter((x) => x.key && x.label) };
      return (editId ? await api.put(`/projects/${editId}`, body) : await api.post("/projects", body)).data;
    },
    onSuccess: () => { toast.success(editId ? "Project updated" : "Project created"); qc.invalidateQueries({ queryKey: ["projects"] }); setOpen(false); },
    onError: onErr,
  });

  const valid = f.name && f.city && f.centre_latitude !== "" && f.centre_longitude !== "" && f.start_date && f.end_date;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-slate-500">A project fixes the city, period and the deliverable unit that providers are paid for.</p>
        <Button className="bg-navy hover:bg-navy-700" onClick={startNew} data-testid="add-project"><Plus className="h-4 w-4 mr-1" /> New Project</Button>
      </div>
      {isLoading ? <Loading /> : !projects?.length ? (
        <EmptyState icon={FolderKanban} title="No projects yet" hint="Create your first project, e.g. NIDAAN Nagpur (TB active case finding)." />
      ) : (
        <Card className="border-slate-200 shadow-none overflow-hidden">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              {["Project", "City", "Client / Funder", "Period", "Unit", "Status", ""].map((h) => <TableHead key={h} className="overline text-slate-500">{h}</TableHead>)}
            </TableRow></TableHeader>
            <TableBody>
              {projects.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="font-medium text-navy">{p.name}<div className="text-xs text-slate-400">{p.code}</div></TableCell>
                  <TableCell>{p.city}{p.state ? `, ${p.state}` : ""}<div className="text-xs text-slate-400">within {p.radius_km} km</div></TableCell>
                  <TableCell className="text-slate-600">{p.client || "—"}</TableCell>
                  <TableCell className="text-sm text-slate-600 whitespace-nowrap">{p.start_date} → {p.end_date}</TableCell>
                  <TableCell className="text-slate-600">{p.unit_label}</TableCell>
                  <TableCell><StatusBadge status={p.status} /></TableCell>
                  <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => startEdit(p)}><Pencil className="h-4 w-4" /></Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit Project" : "New Project"}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Project name *</Label><Input className="mt-1" value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="NIDAAN Nagpur" /></div>
            <div><Label>Code</Label><Input className="mt-1" value={f.code} onChange={(e) => set("code", e.target.value)} placeholder="NIDAAN-NGP" /></div>
            <div className="sm:col-span-2"><Label>Client / Funder</Label><Input className="mt-1" value={f.client} onChange={(e) => set("client", e.target.value)} placeholder="NTEP – Nagpur Municipal Corporation" /></div>
            <div><Label>City *</Label><Input className="mt-1" value={f.city} onChange={(e) => set("city", e.target.value)} /></div>
            <div><Label>State</Label><Input className="mt-1" value={f.state} onChange={(e) => set("state", e.target.value)} /></div>
            <div><Label>City centre latitude *</Label><Input className="mt-1" value={f.centre_latitude} onChange={(e) => set("centre_latitude", e.target.value)} placeholder="21.1458" /></div>
            <div><Label>City centre longitude *</Label><Input className="mt-1" value={f.centre_longitude} onChange={(e) => set("centre_longitude", e.target.value)} placeholder="79.0882" /></div>
            <div><Label>City radius (km)</Label><Input type="number" className="mt-1" value={f.radius_km} onChange={(e) => set("radius_km", e.target.value)} /></div>
            <div className="flex items-end"><Button type="button" variant="outline" className="w-full" onClick={useMyLocation}><LocateFixed className="h-4 w-4 mr-2" /> Use my location</Button></div>
            <div><Label>Start date *</Label><Input type="date" className="mt-1" value={f.start_date} onChange={(e) => set("start_date", e.target.value)} /></div>
            <div><Label>End date *</Label><Input type="date" className="mt-1" value={f.end_date} onChange={(e) => set("end_date", e.target.value)} /></div>
            <div><Label>Billable unit</Label><Input className="mt-1" value={f.unit_label} onChange={(e) => set("unit_label", e.target.value)} /></div>
            <div><Label>Coordinator (internal)</Label><Input className="mt-1" value={f.coordinator_name} onChange={(e) => set("coordinator_name", e.target.value)} /></div>
            <div>
              <Label>Status</Label>
              <Select value={f.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="Active">Active</SelectItem><SelectItem value="Closed">Closed</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-4 border-t pt-3">
            <div className="flex items-center justify-between mb-2">
              <Label>Extra counts on each field log</Label>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => set("log_fields", TB_ACF_FIELDS)}>Use TB ACF template</Button>
                <Button size="sm" variant="outline" onClick={() => set("log_fields", [...f.log_fields, { key: "", label: "" }])}><Plus className="h-3 w-3" /></Button>
              </div>
            </div>
            {f.log_fields.length === 0 && <p className="text-xs text-slate-400">None — providers will only report the billable unit.</p>}
            {f.log_fields.map((lf, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 mb-2">
                <Input placeholder="Label, e.g. Households visited" value={lf.label} onChange={(e) => {
                  const label = e.target.value;
                  const key = lf.key && lf.key !== slug(lf.label) ? lf.key : slug(label);
                  set("log_fields", f.log_fields.map((x, j) => (j === i ? { key, label } : x)));
                }} />
                <Input placeholder="key" className="font-mono text-xs" value={lf.key} onChange={(e) => set("log_fields", f.log_fields.map((x, j) => (j === i ? { ...x, key: e.target.value } : x)))} />
                <Button size="sm" variant="ghost" className="text-red-500" onClick={() => set("log_fields", f.log_fields.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-navy hover:bg-navy-700" disabled={!valid || save.isPending} onClick={() => save.mutate()}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function slug(s) {
  const k = (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  return /^[a-z]/.test(k) ? k : k ? `f_${k}` : "";
}
